import { SyntheticEvent } from 'react';

// eslint-disable-next-line no-restricted-imports
import {
  Autocomplete as AutocompleteBase,
  AutocompleteInputChangeReason,
} from '@mui/material';
import { GlobalClasses, lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';

import { Icon } from '../Icon';
import { Input } from '../Input';
import { Typography } from '../Typography';

import { AutocompleteStyled } from './Autocomplete.styles';
import { AutocompleteProps, AutocompleteValue } from './types';

/**
 * Autocomplete component.
 */
export const Autocomplete = <
  Value extends object,
  Multiple extends boolean | undefined = false
>({
  fullWidth = true,
  value,
  label,
  placeholder,
  loading,
  loadingText,
  error,
  helperText,
  options,
  name,
  optionLabelKey,
  optionValueKey,
  className,
  disabled,
  hideClearIcon,
  variant,
  multiple,
  showOptionalMark,
  showRequiredMark,
  freeSolo,
  disabledFilterOptions,
  readOnly,
  onChange,
  getOptionDisabled,
  onInputChange,
  onClose,
}: AutocompleteProps<Value, Multiple>) => {
  const { t } = useTranslation();

  const handleOnInputChange = (
    event: SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    if (reason === 'reset') {
      return;
    }
    if (!value && freeSolo) {
      onChange?.(null as AutocompleteValue<Value, Multiple>);
    }
    onInputChange?.(event, value);
  };

  return (
    <AutocompleteStyled readOnly={readOnly}>
      <AutocompleteBase
        className={className}
        clearIcon={
          hideClearIcon ? null : (
            <Icon.Close className={GlobalClasses.hovered} size={16} />
          )
        }
        disableClearable={hideClearIcon}
        disableCloseOnSelect={multiple}
        disabled={disabled}
        filterOptions={(options, state) => {
          if (disabledFilterOptions) {
            return options;
          }
          return lodash.searchBy(options, state.inputValue, (option) => {
            const optionValue =
              typeof option === 'object' && option !== null
                ? option[optionValueKey]
                : option;
            return String(optionValue);
          });
        }}
        forcePopupIcon={!readOnly}
        freeSolo={freeSolo}
        fullWidth={fullWidth}
        getOptionDisabled={getOptionDisabled}
        getOptionLabel={(v) =>
          (typeof v === 'string' ? v : v[optionLabelKey]) as string
        }
        isOptionEqualToValue={(v, option) => {
          const optionValue =
            typeof option === 'object' && option !== null
              ? option[optionValueKey]
              : option;
          const value =
            typeof option === 'object' && option !== null
              ? v[optionValueKey]
              : v;
          return value === optionValue;
        }}
        loading={loading}
        loadingText={loadingText || `${t('common.loading')}...`}
        multiple={multiple}
        noOptionsText={t('components.autocomplete.noOptionsText')}
        options={options}
        popupIcon={
          readOnly || disabled ? null : (
            <Icon.ArrowDown className={GlobalClasses.hovered} size={24} />
          )
        }
        readOnly={readOnly}
        renderInput={(props) => {
          const { inputProps } = props;
          inputProps.readOnly = variant === 'select';

          props.inputProps = inputProps;
          props.inputProps.readOnly = readOnly || variant === 'select';
          return (
            <Input
              {...props}
              error={error}
              helperText={helperText}
              label={label}
              name={name}
              placeholder={
                value instanceof Array && value.length > 0
                  ? undefined
                  : placeholder
              }
              readOnly={readOnly}
              showOptionalMark={showOptionalMark}
              showRequiredMark={showRequiredMark}
              onChangeText={(inputValue) => {
                if (freeSolo) {
                  onChange?.({
                    [optionLabelKey]: inputValue,
                    [optionValueKey]: inputValue,
                  } as AutocompleteValue<Value, Multiple>);
                }
              }}
            />
          );
        }}
        renderOption={(props, option) => {
          const value =
            typeof option === 'object' && option !== null
              ? option[optionValueKey]
              : option;
          const label =
            typeof option === 'object' && option !== null
              ? option[optionLabelKey]
              : option;
          return (
            <li {...props} key={String(value)}>
              {label as string}
            </li>
          );
        }}
        renderTags={(value) => {
          const labels = value.map(
            (option) => option[optionLabelKey] as string
          );
          labels.join(', ');

          return (
            <Typography className="autocomplete-tag">
              {labels.join(', ')}
            </Typography>
          );
        }}
        slotProps={{
          popupIndicator: { disableRipple: true },
          clearIndicator: { disableRipple: true },
        }}
        sx={{
          "& button[title='Clear']": {
            visibility: 'visible',
          },
        }}
        value={value}
        onChange={(e, v) => {
          if (freeSolo && typeof v === 'string') {
            onChange?.(
              {
                [optionLabelKey]: v,
                [optionValueKey]: v,
              } as AutocompleteValue<Value, Multiple>,
              e
            );
          } else {
            onChange?.(v as AutocompleteValue<Value, Multiple>, e);
          }
        }}
        onClose={onClose}
        onInputChange={handleOnInputChange}
      />
    </AutocompleteStyled>
  );
};
